// Zebra Browser Print integration (free local utility). The agent listens on
// localhost; we try the HTTPS port first (works from an https app) then HTTP.
// NOTE: must use the hostname "localhost" (not 127.0.0.1) — Browser Print's
// bundled SSL cert is issued for "localhost", so 127.0.0.1 fails the TLS check.
const HOSTS = ["https://localhost:9101", "http://localhost:9100"];

import { recordLabelsSent } from "@/lib/labelCounter";
import { resolveDeviceForMedia, mediaMismatch, hydratePrinterConfig } from "@/lib/printerMap";
import { mediaLabelOf } from "@/lib/tagMedia";

// Manual per-print override set by the print-confirm dialog's printer dropdown.
// Takes priority over the media→printer map for the single job it wraps, then clears.
let _selectedDevice = null;
export function setSelectedDevice(d) { _selectedDevice = d || null; }
export function clearSelectedDevice() { _selectedDevice = null; }


// Thrown when the operator hits STOP mid-print. Carries how many labels were
// actually sent so callers can report "stopped after X of Y".
export class PrintAborted extends Error {
  constructor(done = 0, total = 0) {
    super("Print stopped");
    this.name = "PrintAborted";
    this.aborted = true;
    this.done = done;
    this.total = total;
  }
}

// setTimeout that resolves normally, or rejects immediately if the signal fires
// — so the pacing gap between labels can't swallow a STOP click.
function abortableSleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new PrintAborted());
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener("abort", () => { clearTimeout(t); reject(new PrintAborted()); }, { once: true });
    }
  });
}

async function req(path, opts = {}) {
  let lastErr;
  for (const host of HOSTS) {
    try {
      const res = await fetch(host + path, { ...opts, mode: "cors" });
      if (res.ok) return res;
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      if (e.name === "AbortError") throw new PrintAborted();  // STOP → don't retry the next host
      lastErr = e;
    }
  }
  throw lastErr || new Error("Browser Print not reachable");
}

// All Zebra printers Browser Print currently sees (each: {uid, name, connection, ...}).
// Cached + deduped: dozens of components (one per truck/location row) probe on mount, and
// when Browser Print is absent every probe logs failed-fetch console noise. One shared
// probe serves them all; successes stay fresh for 4s, failures for 15s. `fresh=true`
// bypasses the cache for anything that GATES a real print — a genuine outage always
// surfaces there, never a stale verdict.
let _devCache = { at: 0, ok: false, list: null, err: null };
let _devInflight = null;
export async function listDevices(fresh = false) {
  const ttl = _devCache.ok ? 4000 : 15000;
  if (!fresh && _devCache.at && Date.now() - _devCache.at < ttl) {
    if (_devCache.ok) return _devCache.list;
    throw _devCache.err;
  }
  if (_devInflight) return _devInflight;
  _devInflight = (async () => {
    try {
      const res = await req("/available");
      const data = await res.json();
      let list = Array.isArray(data && data.printer) ? data.printer : [];
      if (!list.length && data && typeof data === "object") {
        for (const v of Object.values(data)) if (Array.isArray(v)) list = list.concat(v);
      }
      const out = list.filter((d) => d && d.uid);
      _devCache = { at: Date.now(), ok: true, list: out, err: null };
      return out;
    } catch (e) {
      _devCache = { at: Date.now(), ok: false, list: null, err: e };
      throw e;
    } finally {
      _devInflight = null;
    }
  })();
  return _devInflight;
}

// THE chokepoint every print path goes through. Two hard rules, in one place, so a new
// print path inherits both for free:
//   1. A job MUST declare its media. No media = nothing to check = refuse (there is no
//      "first available" and no "only one printer so it must be fine" shortcut).
//   2. The target printer must HOLD that stock. A pinned override that holds different
//      stock is REFUSED with a message naming both sides — never silently re-routed and
//      never squeezed onto the wrong stock.
async function resolveDevice(mediaType) {
  if (!mediaType) {
    throw new Error("This print job didn't say which stock it's for, so it can't be routed safely. Nothing was printed.");
  }
  if (_selectedDevice) {
    const bad = mediaMismatch(_selectedDevice, mediaType);
    if (bad) throw new Error(bad);
    return _selectedDevice;
  }
  const list = await listDevices();
  if (!list.length) throw new Error("No Zebra printer found in Browser Print. Open Browser Print and confirm your printers are connected, then try again.");
  const m = resolveDeviceForMedia(mediaType, list);
  if (m) return m;
  throw new Error(`No printer is set for ${mediaLabelOf(mediaType)} stock. Nothing was printed. Open Printer Setup and choose which printer holds ${mediaLabelOf(mediaType)}.`);
}

// Printer Setup's "Identify" test ONLY — the one job that genuinely has no media, because
// its whole purpose is "which physical unit is this row?", including a brand-new printer
// with nothing assigned yet. It is a plain text test label (not a real layout), sent to
// the one device the operator tapped, on whatever stock happens to be loaded.
export async function printIdentifyLabel(device, zpl) {
  if (!device || !device.uid) throw new Error("No printer to identify.");
  await req("/write", {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    body: JSON.stringify({ device, data: zpl }),
  });
  recordLabelsSent(1);
  return device;
}

export async function printZpl(zpl, signal = null, mediaType = null) {
  const device = await resolveDevice(mediaType);
  // Send as text/plain to keep this a CORS "simple request" — a JSON
  // Content-Type triggers an OPTIONS preflight that Browser Print doesn't
  // answer, which surfaces as "Failed to fetch".
  await req("/write", {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    body: JSON.stringify({ device, data: zpl }),
    signal,
  });
  recordLabelsSent(1);
  return device;
}

// Print a batch as ONE small /write job PER label — identical to the proven
// single-print path. Paced near the printer's real print speed so a big run
// can't overflow the printer's buffer (sending 200 labels in a burst would
// queue ~40MB in printer RAM). Small batches still feel instant.
// Pass an AbortSignal to make the run STOPPABLE: the loop breaks before the
// next label and any in-flight /write is aborted, throwing PrintAborted.
export async function printZplBatch(zplList, onProgress, gapMs = 1500, signal = null, mediaType = null) {
  const device = await resolveDevice(mediaType);
  for (let i = 0; i < zplList.length; i++) {
    if (signal?.aborted) throw new PrintAborted(i, zplList.length);
    try {
      await req("/write", {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: JSON.stringify({ device, data: zplList[i] }),
        signal,
      });
    } catch (e) {
      if (e instanceof PrintAborted) throw new PrintAborted(i, zplList.length);
      throw e;
    }
    recordLabelsSent(1);
    if (onProgress) onProgress(i + 1, zplList.length);
    if (i < zplList.length - 1) {
      try { await abortableSleep(gapMs, signal); }
      catch { throw new PrintAborted(i + 1, zplList.length); }
    }
  }
  return device;
}

// Print a large run as sequential SUB-RUNS of at most `chunkSize` labels, with a
// short pause between sub-runs. A single uninterrupted run of ~200 can silently
// stall the printer/Browser Print; sub-runs keep each session short and let the
// printer's buffer drain. Progress + STOP are cumulative across the whole run.
export async function printZplChunked(zplList, { chunkSize = 80, onProgress, gapMs = 1500, betweenMs = 1200, signal = null, mediaType = null } = {}) {
  const size = Math.max(1, chunkSize);
  const total = zplList.length;
  let done = 0;
  let lastDevice = null;
  for (let start = 0; start < total; start += size) {
    if (signal?.aborted) throw new PrintAborted(done, total);
    const chunk = zplList.slice(start, start + size);
    try {
      // fresh device per sub-run (printZplBatch re-resolves it) resets the session
      lastDevice = await printZplBatch(chunk, (d) => { if (onProgress) onProgress(done + d, total); }, gapMs, signal, mediaType);
    } catch (e) {
      if (e instanceof PrintAborted) throw new PrintAborted(done + (e.done || 0), total);
      throw e;
    }
    done += chunk.length;
    if (start + size < total) {
      try { await abortableSleep(betweenMs, signal); }
      catch { throw new PrintAborted(done, total); }
    }
  }
  return lastDevice;
}

// Shown after a STOP: the app can only stop labels it hasn't sent yet — labels
// already in the ZT411's own memory keep printing until the buffer is cleared.
export const ZEBRA_FLUSH_HINT =
  "Labels already sent to the printer will keep printing. To kill them at the ZT411: press PAUSE, then hold FEED/CANCEL ~2s to clear the queued job (or power-cycle the printer).";

// Connectivity only — deliberately does NOT name a printer. It used to report the FIRST
// printer Browser Print listed, which is not the unit the job routes to (and reads as a
// serial on units with no friendly name). The destination is shown by the print confirm
// dialog / "Prints to" line, which resolve it from the chosen media.
export async function checkBrowserPrint() {
  try {
    const list = await listDevices(true);   // fresh probe — this gates real print actions
    if (!list.length) throw new Error("No Zebra device found in Browser Print.");
    hydratePrinterConfig();                 // so names are ready wherever this status shows
    return { ok: true, count: list.length };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}
